import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function Article({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>Flipping a House 101</h1>
        <ArticleAuthor />
        <p>
          If you've watched home renovation shows anytime in the last few years,
          you may have watched people purchase houses and update them to
          increase their value. It looks relatively and many people have found
          success in the process, but if you want to do it the right way, it
          takes a lot of time and effort.{" "}
          <a href="https://www.mymillennialguide.com/7-awesome-tips-first-time-home-flippers/">
            Flipping houses
          </a>{" "}
          is not the kind of side hustle you can cut corners with, but there are
          ways to lessen the learning curve while still minimizing your risks.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          How House Flipping Works
        </h2>
        <p>
          House flipping refers to a buyer who purchases older properties in
          need of renovations for low prices, fixes up structural issues,
          beautifies the interior and exterior, and then resells them for a
          profit. Most houses that are flipped are bought through bank short
          sales,{" "}
          <a href="https://www.hud.gov/topics/avoiding_foreclosure/foreclosureprocess">
            foreclosures
          </a>
          , or property auctions.
        </p>
        <p>
          To be successful in{" "}
          <a href="https://www.housedigest.com/964628/how-to-start-flipping-houses/">
            flipping houses
          </a>
          , you need to spend your money wisely by investing in properties in
          need of TLC, but not extensive renovations. After your purchase,
          you'll need to invest more money in renovations to increase its resale
          value and attract potential buyers by listing the house on the market.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Micro-Flipping</h2>
        <p>
          If you're not skilled in taking on{" "}
          <a href="https://www.oldhouseonline.com/repairs-and-how-to/35-tips-for-restoring-old-houses/">
            older houses
          </a>{" "}
          that require a lot of renovations, micro-flipping may be a good
          option. Micro-flipping involves buyers who are looking for a fast sale
          without doing any major home repairs. You'll have to analyze the data
          for the area more stringently with micro-flipping to buy a home below
          market value and quickly flip it for more money. People who are
          successful in this endeavor are able to do this because they manage a
          high volume of transactions.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          How to Start House Flipping in 7 Steps
        </h2>
        <p>
          Get to know the seven essential steps for flipping houses to reduce
          your financial risk while increasing your chances of a successful
          flip.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          1. Know Your Neighborhood
        </h2>
        <p>
          Take the time to research the neighborhood you plan to invest in.
          Choosing the right location is essential because you want a
          neighborhood that is nice enough for people to want to move to, but
          not too expensive to{" "}
          <a href="https://www.thespruce.com/things-to-look-for-house-hunting-1977279">
            buy a house
          </a>{" "}
          for flipping. Working with a real estate agent during this process is
          beneficial because they know the area well and have undoubtedly helped
          plenty of house flippers find their properties, so they can help you
          find what you're looking for.
        </p>
        <p>
          Before you purchase a{" "}
          <a href="https://www.moneyunder30.com/formula-buying-rental-properties">
            property
          </a>
          , bring in a general contractor to do an inspection to help you get an
          estimate. The estimate will help you determine how much work needs to
          be done so you can decide whether or not the house is still within
          your budget.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          2. Use the 70% Rule to Plan Your Budget
        </h2>
        <p>
          Real estate investors commonly use the 70% rule when it comes to the
          purchase price of a house. The 70% rule says that the investor should
          only pay 70% of the{" "}
          <a href="https://www.fool.com/investing/stock-market/market-sectors/real-estate-investing/basics/arv-formula/">
            after-repair value (ARV)
          </a>{" "}
          of a property. This is what the home is worth after the renovation and
          without the costs of repairs.
        </p>
        <p>
          If the AVR of a house is $200,000 but it needs $25,000 in repairs, you
          should{" "}
          <a href="https://mortgage-calculator.net/fix-and-flip-calculator.php">
            your maximum offer{" "}
          </a>
          should be $115,000 for the home. This is because 70% of the AVR is
          $140,000 and subtracting the $25,000 for renovations leaves you with
          $115,000.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          3. Assess Your Skill Set
        </h2>
        <p>
          People with an in-depth knowledge of real estate,{" "}
          <a href="https://www.asid.org/belong/become">design</a>, and
          construction have uniquely suited skills for house flipping, but they
          aren't necessary skills! If you aren't able to do the work yourself,
          it's important that you know how to find professionals who are able to
          do the work for you. You should start the flipping process by
          assembling a team of experts like contractors, lenders, insurance
          agents, and real estate agents to help you successfully find, fix, and
          resell the property.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          4. Finance Your Project
        </h2>
        <p>
          How do you plan to{" "}
          <a href="https://www.thebalancemoney.com/loans-for-flipping-houses-4129189">
            finance
          </a>{" "}
          your house flipping endeavors? Not everybody has the cash on hand
          that's necessary to put into purchasing a property and doing the
          renovations. If that's the case for you, you'll have to get
          pre-approved for a loan. To get pre-approved on a loan, you'll need a
          good credit score and either a 20% down payment or some other type of
          collateral.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          5. Choose and Buy Your House
        </h2>
        <p>
          Next is potentially the hardest part of flipping a house: searching
          for the{" "}
          <a href="https://www.practicalmoneyskills.com/learn/life_events/buying_a_home/choosing_the_right_home">
            perfect property
          </a>
          . Not only do you have to consider how much the house costs, but you
          also have to have a renovation plan in mind so you can consider its
          potential resale value. The goal is to get the highest profit margin
          possible so it's good to look at distressed, foreclosed, and
          fixer-upper homes. A{" "}
          <a href="https://time.com/nextadvisor/mortgages/how-to-choose-a-real-estate-agent/">
            real estate agent
          </a>{" "}
          and a contractor can help you with your decision as they would have a
          better understanding of the extent of the work.
        </p>
        <p>
          Once you've found the perfect property to flip, make an offer and{" "}
          <a href="https://files.consumerfinance.gov/f/documents/cfpb_buying-a-house_mortgage-closing_checklist.pdf">
            close on the house
          </a>
          . Depending on the market and how good of a deal the house is, there
          may be some competition with other buyers and you may have to spend
          more money. Make sure you know how much you can comfortably pay to
          ensure the house is still profitable. If it's not, there's no shame in
          walking away rather than risking losing money on your house-flipping
          endeavors.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          6. Build Sweat Equity
        </h2>
        <p>
          If you have a low budget, save some money by building sweat equity in
          the property. Sweat equity is the unpaid labor put into the project
          with physical labor and mental effort. For many,{" "}
          <a href="https://moneytips.com/what-is-sweat-equity-in-real-estate/">
            sweat equity
          </a>{" "}
          is an important part of building a successful business because it
          gives people the momentum to keep going.
        </p>
        <h2 className={styles["Article__sub-heading"]}>7. Flip the House</h2>
        <p>
          After completing all the necessary repairs and design plans, the next
          step is{" "}
          <a href="https://flippingprosperity.com/average-cost-to-flip-a-3-bedroom-1500-square-foot-house/">
            reselling the house
          </a>
          . As soon as you list the house, time is of the essence as you are
          less likely to get a profit the longer the house sits on the market.
          The goal should be to make your improvements quickly and then put it
          on the market as fast as you can. Working with a{" "}
          <a href="https://www.realestateagents.com/real-estate-agents/scranton-pa">
            real estate agent
          </a>{" "}
          at this point is essential.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Cost of Flipping a House
        </h2>
        <p>
          The cost will vary depending on how much the initial purchase was, the
          cost of repairs, and the necessary time taken to complete the sale,
          but here is a brief overview of the aspects you should expect to pay
          for during this process:
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Your Financial Investment
        </h2>
        <p>
          This process is all about math because you want to be sure you'll earn
          a profit when everything is said and done. Some of the following
          expenses house flippers should be prepared for include:
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.tchabitat.org/blog/home-buying-down-payment">
                Down payment
              </a>
            </p>
          </li>
          <li>
            <p>Inspection fees</p>
          </li>
          <li>
            <p>Insurance payments</p>
          </li>
          <li>
            <p>Property tax</p>
          </li>
          <li>
            <p>
              <a href="https://www.homeclosing101.org/the-closing-process/closing-costs-explained/">
                Closing cost
              </a>
            </p>
          </li>
          <li>
            <p>Marketing cost</p>
          </li>
          <li>
            <p>
              <a href="https://www.realestateagents.com/real-estate-agents/pittsburgh-pa">
                Real estate agent
              </a>{" "}
              fee
            </p>
          </li>
          <li>
            <p>Utility cost</p>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Your Time Investment</h2>
        <p>
          Aside from the financial investment required, there is a significant
          time investment too, which will vary depending on the scope of the
          project. You should expect to spend a minimum of 6 to 12 weeks on the
          process from start to finish if everything goes according to plan.
          However, there is a large chance any part of the process could get
          delayed by several months, which could cause a snag in your plans.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Is Flipping Houses Right for You?
        </h2>
        <p>
          House flipping isn't right for everybody. It's always a good idea to
          weigh the{" "}
          <a href="https://www.investopedia.com/articles/mortgages-real-estate/08/house-flip.asp">
            pros and cons
          </a>{" "}
          before you get started.
        </p>
        <table border="0" cellSpacing="0" cellPadding="3" width="997">
          <colgroup>
            <col width="465" />
            <col width="520" />
          </colgroup>
          <tbody>
            <tr>
              <td width="465">
                <p>
                  <b>Pros</b>
                </p>
              </td>
              <td width="520">
                <p>
                  <b>Cons</b>
                </p>
              </td>
            </tr>
            <tr>
              <td width="465">
                <p>Great profit potential</p>
              </td>
              <td width="520">
                <p>Great potential for loss in investments</p>
              </td>
            </tr>
            <tr>
              <td width="465">
                <p>
                  If you are skilled in design or construction, this is a great
                  side hustle opportunity
                </p>
              </td>
              <td width="520">
                <p>
                  Without the necessary skills, outside help and support can be
                  costly.
                </p>
              </td>
            </tr>
            <tr>
              <td width="465">
                <p>
                  Compared to other real estate investments, house flipping is
                  relatively low risk.
                </p>
              </td>
              <td width="520">
                <p>
                  House flipping requires a lot of planning and budgeting to be
                  done correctly.
                </p>
              </td>
            </tr>
            <tr>
              <td width="465">
                <p>You can learn a lot about the real estate market.</p>
              </td>
              <td width="520">
                <p>
                  Plenty of unexpected costs can come up and you may spend more
                  money than anticipated.
                </p>
              </td>
            </tr>
            <tr>
              <td width="465">
                <p>
                  You can learn a lot about your local market and community.
                </p>
              </td>
              <td width="520">
                <p>
                  You'll need in-depth knowledge of the local real estate
                  industry for the best possible chance.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <h2 className={styles["Article__sub-heading"]}>The Bottom Line</h2>
        <p>
          With a good plan that includes a careful look at your financial
          situation,{" "}
          <a href="https://www.bobvila.com/slideshow/here-s-how-much-house-flippers-actually-make-53102">
            house flipping
          </a>{" "}
          can be a great business option, but it's not one you should take on if
          you're looking for overnight success. There is a great opportunity to
          make costly mistakes and lose money during the process, so it's
          important to work with a team of experts. Working with people who know
          the local real estate market and know how to make repairs can help you
          out in the long run and with the right loan and{" "}
          <a href="https://www.businessinsider.com/personal-finance/average-mortgage-interest-rate">
            interest rate
          </a>
          , you'll have no problem flipping a house!
        </p>
      </>
    );
  };

  return (
    <>
      <Head
        title={"Flipping A House 101 | RealEstateAgents.com"}
        description={
          "Identify the steps for flipping a house and making a major profit."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/flipping-a-house-101",
          },
          headline: "Flipping A House 101",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
